import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { ButtonGroup } from 'src/components/atoms/ButtonGroup';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { Callout } from 'src/components/molecules/Callout';
import { Modal } from 'src/components/molecules/Modal';
import { SlideOut } from 'src/components/molecules/SlideOut/SlideOut';
import { handleErrorToast } from 'src/components/molecules/Toast';
import {
  ManagedChannelType,
  useGetOrganisationFeaturesQuery,
  useGetUserChannelEntriesQuery,
  useGetUserMessagesByReportQuery,
  useMyAccountQuery,
} from 'src/graphql';
import { useSocketMessages, useSocketSendMessage } from 'src/hooks/cases';
import { Feature, FeatureFlag, MessageState } from 'src/types';
import { isBefore } from 'src/utils/time';
import { MessageInput } from '../../ReportMessages/MessageInput';
import { MessagesList } from '../../ReportMessages/MessageList';
import { useManagedChannel } from '../../ReportMessages/useManagedChannel';
import { ReportingReportSummaryCard } from '../ReportingReportSummaryCard/ReportingReportSummaryCard';
import {
  ButtonWrapper,
  Container,
  Header,
  ModalDescription,
  ModalDescriptionBold,
} from './ReportingMessageSlideIn.styles';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  reportDetails: {
    internalId: string;
    reportReadableId: string;
    category: string;
    submittedAt: Date;
    status: string;
  };
  onClose?: () => void;
}

export const ReportingMessageSlideIn = ({
  open,
  setOpen,
  reportDetails,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const TranslationOption = {
    ORIGINAL: t('public.ChatContainer.originalMessages'),
    TRANSLATED: t('public.ChatContainer.translatedMessages'),
  };

  const { data: featuresData } = useGetOrganisationFeaturesQuery();
  const { data: accountSettings } = useMyAccountQuery();
  const defaultTranslationOption = accountSettings?.myAccount
    ?.translationEnabled
    ? TranslationOption.TRANSLATED
    : TranslationOption.ORIGINAL;
  const [seeMoreModalOpen, setSeeMoreModalOpen] = useState<boolean>(false);
  const [draftMessage, setDraftMessage] = useState('');
  const [translationOption, setTranslationOption] = useState(
    defaultTranslationOption,
  );

  useEffect(() => {
    setTranslationOption(defaultTranslationOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountSettings?.myAccount]);

  const isDynamicTranslationsEnabled = isRequiredFeatureEnabled(
    (featuresData?.getOrganisationFeatures as Feature[]) || [],
    FeatureFlag.DYNAMIC_TRANSLATIONS,
  );

  const isTranslated = translationOption === TranslationOption.TRANSLATED;

  const queryClient = useQueryClient();
  const { data: messagesData } = useGetUserMessagesByReportQuery(
    {
      report: reportDetails.internalId,
      translate: isTranslated,
    },
    {
      enabled: open,
    },
  );

  useSocketMessages({
    channel: open ? reportDetails.internalId : null,
    translate: isTranslated,
    queryKey: 'GetUserMessagesByReport',
    context: 'opr',
  });

  const { sendMessage: socketSend, markMessagesAsRead } = useSocketSendMessage({
    context: 'opr',
  });

  const markingAsReadRef = useRef(false);

  useEffect(() => {
    if (!open) {
      markingAsReadRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (
      !open ||
      !messagesData?.getUserMessagesByReport ||
      markingAsReadRef.current
    ) {
      return;
    }

    const hasUnreadMessages = messagesData.getUserMessagesByReport.some(
      (message) => message.state !== MessageState.READ && !message.isReporter,
    );

    if (hasUnreadMessages && markMessagesAsRead(reportDetails.internalId)) {
      markingAsReadRef.current = true;
    }
  }, [open, messagesData, markMessagesAsRead, reportDetails.internalId]);

  useEffect(() => {
    if (!messagesData?.getUserMessagesByReport) {
      return;
    }

    const allRead = messagesData.getUserMessagesByReport.every(
      (message) => message.isReporter || message.state === MessageState.READ,
    );

    if (allRead && markingAsReadRef.current) {
      markingAsReadRef.current = false;
      queryClient.invalidateQueries(['UserReports']);
      queryClient.invalidateQueries([
        'GetReportById',
        { reportId: reportDetails.internalId },
      ]);
    }
  }, [messagesData, queryClient, reportDetails.internalId]);

  const handleSendMessage = () => {
    if (!draftMessage) {
      return;
    }

    const draft = draftMessage;
    const sent = socketSend(
      reportDetails.internalId,
      draft,
      () => setDraftMessage(''),
      () => setDraftMessage(draft),
    );
    if (!sent) {
      handleErrorToast({
        toastMessage: 'Oops, something went wrong. Please try again.',
      });
    }
  };

  const messages = messagesData?.getUserMessagesByReport ?? [];
  const { data } = useGetUserChannelEntriesQuery(
    {
      channel: reportDetails.internalId,
    },
    { enabled: open },
  );

  const { channelEntries } = useManagedChannel(
    (data?.getUserChannelEntries as ManagedChannelType[]) ?? [],
  );

  const messagesWithType = messages.map((message) => ({
    ...message,
    isReporter: !message.isReporter,
    type: 'message' as const,
  }));

  const sortedMessagesAndNotifications = [
    ...messagesWithType,
    ...channelEntries,
  ].sort((a, b) => {
    if (isBefore(a.createdAt, b.createdAt)) {
      return -1;
    }

    return 1;
  });

  const showMoreModal = () => {
    return (
      <Modal
        title={t('public.ChatContainer.titlev2')}
        setOpen={setSeeMoreModalOpen}
        open={seeMoreModalOpen}
        onConfirm={() => setSeeMoreModalOpen(false)}
        size={'medium'}
        data-testid={'reshub-message-translation-learn-more-modal'}
        confirmButtonText={t('public.ChatContainer.understood')}
      >
        <ModalDescription>
          {t('public.ChatContainer.description')}
          <br />
          {t('public.ChatContainer.autoTranslate')}
          <br />
          {t('public.ChatContainer.changeInProfile')}{' '}
          <ModalDescriptionBold>
            {t('public.ChatContainer.caseSettings')}
          </ModalDescriptionBold>
          <br />
          <br />
          {t('public.ChatContainer.descriptionSecondHalf')}
        </ModalDescription>
      </Modal>
    );
  };

  return (
    <SlideOut
      isOpen={open}
      title={t('reporting.MessageSlideIn.title')}
      confirmButtonText={t('reporting.MessageSlideIn.confirmButton')}
      confirmButtonDataTestid="save-summary-changes"
      containerDataTestid="case-summary"
      dividerHasPadding={false}
      showFooter={false}
      onClose={() => {
        setOpen(false);
        if (onClose) {
          onClose();
        }
      }}
      data-testid="report-messages-slideout"
      onConfirm={handleSendMessage}
      applyButtonDisabled={!draftMessage}
      $width={'400px'}
      bodyStyle={{ margin: '0' }}
      headerChildren={
        <>
          <ReportingReportSummaryCard
            reportReadableId={reportDetails.reportReadableId}
            category={reportDetails.category}
            submittedAt={reportDetails.submittedAt}
            status={reportDetails.status}
          />
          {isDynamicTranslationsEnabled && (
            <Header>
              <ButtonWrapper>
                <ButtonGroup
                  options={[
                    TranslationOption.TRANSLATED,
                    TranslationOption.ORIGINAL,
                  ]}
                  selectedOption={translationOption}
                  size="medium"
                  setSelectedOption={(option) => {
                    setTranslationOption(option);
                  }}
                  buttonWidth="180px"
                  data-testid="reshub-message-translation-button"
                  buttonGroupOptionTestId={'opr-login-option'}
                />
              </ButtonWrapper>
              <Callout
                onClick={() => {
                  setSeeMoreModalOpen(true);
                }}
                data-testid="user-message-translation-learn-more-button"
                header={t('reporting.MessageSlideIn.translationHeader')}
              />
            </Header>
          )}
        </>
      }
    >
      <Container>
        <MessagesList messagesList={sortedMessagesAndNotifications} />
        <MessageInput
          updateDraftMessage={(e) => setDraftMessage(e.target.value)}
          sendMessage={handleSendMessage}
          draftMessage={draftMessage}
          isAssigneeOrHasSentMessage={true}
        />
      </Container>
      {showMoreModal()}
    </SlideOut>
  );
};
